import {
  BOOKING_ENDPOINT,
  MutationRequest,
  MutationResponse,
} from '@/features/book-appointment/booking.types';
import { clsx, type ClassValue } from 'clsx';
import { NextApiRequest, NextApiResponse } from 'next/types';
import { BOOKING_PAGE, PROFILE_PAGE, SITECORE_API_URL } from './constants';
import { Address, Contact, User, UserData, UserFacility } from '@/types/UserData';
import { getCustomSetting } from '../../../../packages/mho-core/lib/lib/config';
import { debug } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  Account,
  AccountResponse,
} from '../../../../packages/mho-core/lib/api-service/account/account-types';
import config from '@/temp/config';

export const getBookingPageUrl = (id: string): string => `${BOOKING_PAGE}/${id}`;
export const getPhysicianProfileUrl = (id: string): string => `${PROFILE_PAGE}/${id}`;

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export const isPhysicianSiteType = (siteType: string): boolean => {
  return siteType?.toLowerCase() === 'psg';
};

export const isHospitalSiteType = (siteType: string): boolean => {
  return siteType?.toLowerCase() === 'hospital operations';
};

export const isLocationMarketContext = (marketContext: string): boolean => {
  return marketContext?.toLowerCase() === 'location';
};

export const physicianDisplayTitle = (physicianFullName: string, physicianDesignation: string) =>
  `${physicianFullName}, ${physicianDesignation}`;

/**
 * Default timeout for mutation requests (5 minutes)
 * Healthcare booking workflows may take longer due to multiple backend service calls
 */
const MUTATION_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Creates a fetch request with timeout support
 * @param url - API endpoint URL
 * @param data - Request payload
 * @param timeoutMs - Timeout in milliseconds (default: 5 minutes)
 * @returns Response data
 * @throws Error if request fails or times out
 */
export const createMutation =
  (url: string, timeoutMs: number = MUTATION_TIMEOUT_MS) =>
  async (data: MutationRequest): Promise<MutationResponse> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`, {
          cause: response.status,
        });
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(
          `Request timeout after ${timeoutMs / 1000} seconds. The booking may still be processing on the server.`,
          { cause: 'TIMEOUT' }
        );
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

export const createApiEndpoint = (url: BOOKING_ENDPOINT) =>
  async function handler(req: NextApiRequest, res: NextApiResponse): Promise<void> {
    if (req.method === 'POST') {
      try {
        const data = req.body;

        if (!data) {
          res.status(400).json({ error: 'Invalid data format' });
        }

        // Request to an external backend service
        const externalResponse = await fetch(
          `${process.env.SITECORE_API_HOST}/${SITECORE_API_URL}/${url}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          }
        );

        if (!externalResponse.ok) {
          throw new Error(
            `Failed to fetch: ${externalResponse.status} ${externalResponse.statusText}`
          );
        }

        const externalData = await externalResponse.json();

        return res.status(200).json(externalData);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        return res.status(500).json({ error: 'Internal Server Error', details: errorMessage });
      }
    } else {
      // Method not allowed if not POST
      return res.status(405).json({ error: 'Method Not Allowed' });
    }
  };

const populateUserData = (account: Account): UserData => {
  const user: User = {
    myHealthOneId: account.user.myHealthOneId,
    firstName: account.user.firstName,
    middleName: account.user.middleName ?? null,
    lastName: account.user.lastName,
    gender:
      account.user.gender?.toUpperCase() === 'MALE'
        ? 'Male'
        : account.user.gender?.toUpperCase() === 'FEMALE'
          ? 'Female'
          : 'Unknown',
    dob: account.user.dob ?? '',
  };
  const addresses: Address[] = account.addresses
    ? account.addresses.map((a) => {
        return {
          addressLine1: a.addressLine1,
          addressLine2: a.addressLine2,
          city: a.city,
          zip: a.zip,
          state: a.state,
          addressType: a.addressType?.toUpperCase() === 'PRIMARY' ? 'Primary' : 'Mailing',
        };
      })
    : [];
  const contacts: Contact[] = account.contacts
    ? account.contacts.map((c) => {
        return {
          contactType: c.contactInfo.indexOf('@') >= 0 ? 'Email' : 'Cell',
          contactInfo: c.contactInfo,
          isPreferred: c.isPreferred,
        };
      })
    : [];
  const facility: UserFacility = {
    facilityCoid: account.user.preferredFacility?.coid ?? '',
    facilityName: account.user.preferredFacility?.displayName ?? '',
    marketName: account.user.preferredFacility?.market ?? '',
  };
  const userData: UserData = {
    user: user,
    addresses: addresses,
    contacts: contacts,
    facility: facility,
  };
  return userData;
};

export const getUserData = async (
  cookies: Partial<{ [key: string]: string }>
): Promise<UserData | undefined> => {
  const mhoIsatKey = getCustomSetting('CookieMhoToken');
  const mhoIsrcKey = getCustomSetting('CookieUsername');
  const mhoIsatValue = cookies?.[mhoIsatKey];
  const mhoIsrcValue = cookies?.[mhoIsrcKey];

  if (mhoIsatValue && mhoIsrcValue) {
    try {
      const response = await fetch(`${config.mhoUrl}/api/accounts?levelOfDetail=FULL`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          cookie: `${mhoIsatKey}=${mhoIsatValue};${mhoIsrcKey}=${mhoIsrcValue}`,
        },
      });
      let accountResponse;
      try {
        accountResponse = await response.json();
      } catch (error) {
        debug.layout('Failed to parse account response as JSON:', error);
        return;
      }
      if (
        (accountResponse as AccountResponse)?.status?.code === 200 &&
        (accountResponse as AccountResponse)?.data
      ) {
        const account = (accountResponse as AccountResponse).data;
        return populateUserData(account);
      } else {
        debug.layout('Error while getting MHO account data:', accountResponse);
        return;
      }
    } catch (error) {
      debug.layout('Network error while fetching MHO account data:', error);
    }
  }
  return;
};
